import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { FrontendProduct } from "@redotech/redo-model/agents/support-agents/frontend-product";
import { SubscribeToBackInStockMessageType } from "@redotech/redo-model/conversation/message-parsers/subscribe-to-back-in-stock-message-parser";
import { input, nonEmptyValidator } from "@redotech/ui/form";
import { optionalEmailValidator } from "@redotech/web-util/form";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { memo, useMemo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../../arbiter-components/input/redo-text-input";
import { RedoListItem } from "../../arbiter-components/list/redo-list";
import { RedoSpinner } from "../../arbiter-components/loading/redo-spinner";
import { RedoSingleSelectDropdownInput } from "../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../../flex";
import { ProductByIdFetcher } from "../../order-editing/hooks/use-products";
import { Text } from "../../text";
import { HandleBackInStockSubscribeFn } from "../common";
import { ChatContentRenderer } from "./chat-content-renderer";
import {
  GenericConfirmedState,
  GenericRejectedState,
} from "./chat-content-states";
import * as css from "./subscribe-to-back-in-stock-tool-message.module.css";
import { getConfirmedStateContent } from "./tool-message-utils";

const emailInput = input<string>({
  validator: (value: string) => [
    ...optionalEmailValidator(value),
    ...nonEmptyValidator(value),
  ],
});

interface BackInStockProductSelectorProps {
  product: FrontendProduct;
  conversationId: string;
  messageId: string;
  pendingToolCallId?: string;
  onSubscribe: HandleBackInStockSubscribeFn;
  readOnly: boolean;
  initialEmail?: string;
  initialVariantId?: string;
  messageSide?: "left" | "right";
}

const BackInStockProductSelector = memo(function BackInStockProductSelector({
  product,
  conversationId,
  messageId,
  pendingToolCallId,
  onSubscribe,
  readOnly,
  initialEmail,
  initialVariantId,
  messageSide = "left",
}: BackInStockProductSelectorProps) {
  const emailInputState = useInput(emailInput, initialEmail ?? "");

  const variantOptions: RedoListItem<{ id: string; title: string }>[] =
    useMemo(() => {
      return product.variants.map((variant) => {
        const variantText =
          variant.attributes
            ?.map((attr) => `${attr.name}: ${attr.value}`)
            .join(" / ") || "Default";
        const displayText = variant.available
          ? `${variantText} (In Stock)`
          : variantText;

        return {
          id: variant.externalId,
          value: { id: variant.externalId, title: variantText },
          type: "text",
          text: displayText,
        };
      });
    }, [product.variants]);

  const [selectedVariant, setSelectedVariant] = useState<
    RedoListItem<{ id: string; title: string }> | undefined
  >(() => {
    if (initialVariantId) {
      const found = variantOptions.find((opt) => opt.id === initialVariantId);
      if (found) return found;
    }
    const firstUnavailableVariant = product.variants.find((v) => !v.available);
    const targetVariantId =
      firstUnavailableVariant?.externalId || product.variants[0]?.externalId;
    return variantOptions.find((opt) => opt.id === targetVariantId);
  });

  const [subscribeLoad, doSubscribe] = useTriggerLoad(async () => {
    if (!selectedVariant?.value.id) return;
    await onSubscribe({
      conversationId,
      messageId,
      customerEmail: emailInputState.value,
      pendingToolCallId,
      productId: product.externalId,
      productName: product.name,
      variantId: selectedVariant.value.id,
    });
  });

  const handleSubmit = useHandler((event?: React.FormEvent<HTMLElement>) => {
    event?.preventDefault();
    if (emailInputState.allErrors.length > 0 || !selectedVariant) return;
    doSubscribe();
  });

  const disabled = readOnly || subscribeLoad.pending || !onSubscribe;
  const imageUrl =
    product.images?.[0]?.url || product.variants[0]?.images?.[0]?.url;

  return (
    <Flex
      as="form"
      className={classNames(css.subscribeToBackInStockBubbleContainer, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.messageSideLeft]: messageSide === "left",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.messageSideRight]: messageSide === "right",
      })}
      dir="column"
      gap="lg"
      onSubmit={handleSubmit}
      p="lg"
    >
      <Flex dir="row" gap="lg">
        {imageUrl && (
          <Flex shrink={0} style={{ width: 80, height: 80 }}>
            <img
              alt={product.name}
              src={imageUrl}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "var(--redo-corner-small-radius)",
              }}
            />
          </Flex>
        )}
        <Flex dir="column" gap="sm" grow={1} justify="center">
          <Text fontSize="sm" fontWeight="semibold">
            {product.name}
          </Text>
          <Text color="secondary" fontSize="xs">
            Get notified when back in stock
          </Text>
        </Flex>
      </Flex>

      {product.variants.length > 1 && (
        <Flex dir="column" gap="xs">
          <Text color="secondary" fontSize="xs">
            Select variant
          </Text>
          <RedoSingleSelectDropdownInput
            disabledItems={product.variants
              .filter((v) => v.available)
              .map((v) => v.externalId)}
            disabledTooltips={product.variants
              .filter((v) => v.available)
              .reduce(
                (acc, v) => {
                  acc[v.externalId] = "Already in stock";
                  return acc;
                },
                {} as Record<string, string>,
              )}
            options={variantOptions}
            optionSelected={({ item }) => setSelectedVariant(item)}
            placeholder="Select variant"
            selectedItem={selectedVariant}
          />
        </Flex>
      )}

      <Flex align="flex-end" dir="row" gap="md">
        <Flex dir="column" grow={1}>
          <RedoTextInput
            label="Email"
            placeholder="Enter your email"
            setValue={emailInputState.setValue}
            state={disabled ? "disabled" : undefined}
            value={emailInputState.value}
          />
        </Flex>
        <RedoButton
          disabled={
            disabled || emailInputState.allErrors.length > 0 || !selectedVariant
          }
          hierarchy="primary"
          onClick={() => handleSubmit()}
          pending={subscribeLoad.pending}
          text="Subscribe"
        />
      </Flex>
    </Flex>
  );
});

interface BackInStockLoadingStateProps {
  messageSide?: "left" | "right";
}

const BackInStockLoadingState = memo(function BackInStockLoadingState({
  messageSide = "left",
}: BackInStockLoadingStateProps) {
  return (
    <Flex
      align="center"
      className={classNames(css.subscribeToBackInStockBubbleContainer, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.messageSideLeft]: messageSide === "left",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.messageSideRight]: messageSide === "right",
      })}
      dir="row"
      gap="md"
      p="lg"
    >
      <RedoSpinner size="xs" />
      <Text color="secondary" fontSize="sm">
        Loading product...
      </Text>
    </Flex>
  );
});

interface BackInStockErrorStateProps {
  messageSide?: "left" | "right";
}

const BackInStockErrorState = memo(function BackInStockErrorState({
  messageSide = "left",
}: BackInStockErrorStateProps) {
  return (
    <Flex
      className={classNames(css.subscribeToBackInStockBubbleContainer, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.messageSideLeft]: messageSide === "left",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.messageSideRight]: messageSide === "right",
      })}
      dir="column"
      gap="md"
      p="lg"
    >
      <Text color="secondary" fontSize="sm">
        Unable to load product information
      </Text>
    </Flex>
  );
});

interface BackInStockAllInStockStateProps {
  messageSide?: "left" | "right";
  productName: string;
}

const BackInStockAllInStockState = memo(function BackInStockAllInStockState({
  messageSide = "left",
  productName,
}: BackInStockAllInStockStateProps) {
  return (
    <Flex
      className={classNames(css.subscribeToBackInStockBubbleContainer, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.messageSideLeft]: messageSide === "left",
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [css.messageSideRight]: messageSide === "right",
      })}
      dir="column"
      gap="md"
      p="lg"
    >
      <Text fontSize="sm" fontWeight="semibold">
        {productName}
      </Text>
      <Text color="secondary" fontSize="sm">
        All variants are currently in stock
      </Text>
    </Flex>
  );
});

export interface CurrentProductInfoFallback {
  productId: string;
  productName: string;
  variantId?: string;
}

export const SubscribeBackInStockToolMessage = memo(
  function SubscribeBackInStockToolMessage({
    message,
    readOnly,
    onSubscribe,
    conversationId,
    messageSide = "left",
    productFetcher,
    teamId,
    currentProductInfo,
  }: {
    message: SubscribeToBackInStockMessageType;
    readOnly: boolean;
    onSubscribe?: HandleBackInStockSubscribeFn;
    conversationId: string;
    messageSide?: "left" | "right";
    productFetcher?: ProductByIdFetcher;
    teamId?: string;
    currentProductInfo?: CurrentProductInfoFallback;
  }) {
    const structuredContent = message.structuredContent;
    // Use productId from structuredContent if available, otherwise fall back to current product context
    const productId =
      structuredContent?.productId ?? currentProductInfo?.productId;
    const initialVariantIds = structuredContent?.variantIds;
    // Fall back to current product's variant if no variant in structuredContent
    const fallbackVariantId = currentProductInfo?.variantId;
    const initialEmail = structuredContent?.customerEmail;

    const { data: product, isLoading } = useQuery({
      queryKey: ["backInStockProduct", teamId, productId],
      queryFn: async () => {
        const products = await productFetcher!([productId!], undefined, {
          includeOutOfStock: true,
        });
        return products[0] ?? null;
      },
      enabled: !!productId && !!productFetcher && !!teamId,
    });

    const variantCount = initialVariantIds?.length || 0;

    const { title: confirmedTitle, subtitle: confirmedSubtitle } =
      getConfirmedStateContent(
        message,
        "Successfully subscribed to back in stock notifications",
        "Something went wrong subscribing to back in stock notifications.",
        `We'll notify you at ${initialEmail} when ${variantCount > 1 ? "these items are" : "this item is"} back in stock`,
      );

    const renderPendingContent = () => {
      if (!productId || !productFetcher || !teamId) {
        return <BackInStockErrorState messageSide={messageSide} />;
      }

      if (isLoading) {
        return <BackInStockLoadingState messageSide={messageSide} />;
      }

      if (!product) {
        return <BackInStockErrorState messageSide={messageSide} />;
      }

      const allVariantsInStock = product.variants.every((v) => v.available);
      if (allVariantsInStock) {
        return (
          <BackInStockAllInStockState
            messageSide={messageSide}
            productName={product.name}
          />
        );
      }

      return (
        <BackInStockProductSelector
          conversationId={conversationId}
          initialEmail={initialEmail ?? undefined}
          initialVariantId={initialVariantIds?.[0] ?? fallbackVariantId}
          messageId={message._id}
          messageSide={messageSide}
          onSubscribe={onSubscribe!}
          pendingToolCallId={message.pendingToolCallId ?? undefined}
          product={product}
          readOnly={readOnly || !onSubscribe}
        />
      );
    };

    return (
      <ChatContentRenderer
        contentByState={{
          pending: renderPendingContent(),
          confirmed: (
            <GenericConfirmedState
              subtitle={confirmedSubtitle}
              title={confirmedTitle}
            />
          ),
          rejected: (
            <GenericRejectedState title="Unable to subscribe to notifications" />
          ),
        }}
        message={message}
      />
    );
  },
);
